import React from 'react'
import { Progress, Steps } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { Stack, Typography } from '@sureco/design-system'

export type WorkflowStepState = 'pending' | 'active' | 'done' | 'error'

export interface WorkflowStep {
  key: string
  title: string
  state: WorkflowStepState
  /** One-line description shown under the step title (counts / sub-status). */
  detail?: string
  /** 0-100; rendered as a thin bar under the active step only. */
  percent?: number
}

interface ProcessingStatusCardProps {
  steps: WorkflowStep[]
  modelLabel: string
  benchmarkLabel: string
  startedOn: string
  lastUpdated: string
  status?: string
  title?: string
  error?: string
}

const STEP_STATUS: Record<
  WorkflowStepState,
  'finish' | 'process' | 'wait' | 'error'
> = {
  done: 'finish',
  active: 'process',
  pending: 'wait',
  error: 'error',
}

function getStatusColor(status?: string): string {
  switch (status) {
    case 'processing':
    case 'queued':
      return '#fa8c16'
    case 'completed':
      return '#98ce24'
    case 'failed':
      return '#ff4d4f'
    default:
      return '#52c41a'
  }
}

function getStatusLabel(status?: string): string {
  if (!status) return 'Processing'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

// Keyframes for the live status dot.
const pulseAnimation = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`

export default function ProcessingStatusCard({
  steps,
  modelLabel,
  benchmarkLabel,
  startedOn,
  lastUpdated,
  status,
  title = 'Applying contribution model',
  error,
}: ProcessingStatusCardProps) {
  const isLive = status === 'processing' || status === 'queued'
  const accent = getStatusColor(status)

  const items = steps.map((step) => ({
    title: step.title,
    status: STEP_STATUS[step.state],
    icon: step.state === 'active' ? <LoadingOutlined /> : undefined,
    description:
      step.detail || step.percent != null ? (
        <Stack space={6}>
          {step.detail ? (
            <Typography size={13} color="#595959">
              {step.detail}
            </Typography>
          ) : null}
          {step.state === 'active' && step.percent != null ? (
            <Progress
              percent={step.percent}
              size="small"
              strokeColor={accent}
              status="active"
            />
          ) : null}
        </Stack>
      ) : undefined,
  }))

  return (
    <>
      <style>{pulseAnimation}</style>
      <div
        style={{
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          padding: '24px',
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        }}
      >
        <Stack space={20}>
          {/* Header */}
          <Stack direction="row" align="center" justify="space-between">
            <Stack direction="row" align="center" space={8}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: accent,
                  animation: isLive
                    ? 'pulse 1.5s ease-in-out infinite'
                    : 'none',
                }}
              />
              <Stack space={4}>
                <Typography size={20} bold>
                  {title}
                </Typography>
                <Typography size={14} color="#595959">
                  {getStatusLabel(status)}
                </Typography>
              </Stack>
            </Stack>
            <Typography size={14} color="#8c8c8c">
              Last updated: {lastUpdated}
            </Typography>
          </Stack>

          {/* Workflow timeline */}
          <Steps direction="vertical" size="small" items={items} />

          {error ? (
            <Typography size={14} color="#ff4d4f">
              {error}
            </Typography>
          ) : null}

          {/* Details */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 24,
              borderTop: '1px solid #f0f0f0',
              paddingTop: 16,
            }}
          >
            <Stack space={8}>
              <Typography size={14} color="#8c8c8c">
                Model Name
              </Typography>
              <Typography size={16} bold>
                {modelLabel}
              </Typography>
            </Stack>

            <Stack space={8}>
              <Typography size={14} color="#8c8c8c">
                Benchmark Snapshot Name
              </Typography>
              <Typography size={16} bold>
                {benchmarkLabel}
              </Typography>
            </Stack>

            <Stack space={8}>
              <Typography size={14} color="#8c8c8c">
                Started On
              </Typography>
              <Typography size={16} bold>
                {startedOn}
              </Typography>
            </Stack>
          </div>
        </Stack>
      </div>
    </>
  )
}
